import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { toast } from "react-toastify";
import ListContent from "~/components/Organization/listContent";
import { GuestList } from "~/components/Organization/listContent/GuestList";
import { MemberList } from "~/components/Organization/listContent/MemberList";
import {
  clearError,
  fetchOrganization,
  fetchOrganizationGuests,
  fetchOrganizationMembers,
} from "~/slices/organizationSlice";
import type { AppDispatch, RootState } from "~/store";
import styles from "./index.module.scss";

const OrganizationTop: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const { members, guests, isLoading, error } = useSelector(
    (state: RootState) => state.organization,
  );
  const dispatch = useDispatch<AppDispatch>();

  const tabIds = useMemo(() => ["info", "members", "guests"], []);

  // 現在のパスから組織ID（ベースURL部分）を取得
  const pathSegments = location.pathname.split("/");
  const basePath = pathSegments.slice(0, 3).join("/"); // ex: "/JVCFH7Cy4nVE3yNQ"

  // 現在のパスからタブを選択
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const currentPath = pathSegments.slice(3).join("/"); // "organization" or "organization/members"
    const tabIndex = tabIds.indexOf(currentPath || tabIds[0]!); // タブIDのリストに一致するものを探す
    setSelectedIndex(tabIndex !== -1 ? tabIndex : 0);
  }, [pathSegments, tabIds]);

  const handleTabSelect = (index: number) => {
    setSelectedIndex(index);
    navigate(`${basePath}/${tabIds[index]!}`); // ベースパスを維持しながら遷移
  };

  const tabTitles = ["詳細情報", "メンバー", "ゲスト"];

  useEffect(() => {
    // 組織情報の取得
    dispatch(fetchOrganization(currentUser.organization_id));

    // メンバーとゲストの取得（初回のみ実行）
    if (members.length === 0) {
      dispatch(fetchOrganizationMembers(currentUser.organization_id));
    }
    if (guests.length === 0) {
      dispatch(fetchOrganizationGuests(currentUser.organization_id));
    }
  }, [currentUser.organization_id, dispatch, members.length, guests.length]);

  useEffect(() => {
    // エラーハンドリング
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  return (
    <div data-testid="organization-page">
      <h1 className={styles["pageTitle"]}>組織</h1>
      <Tabs className={styles["tab"]} selectedIndex={selectedIndex} onSelect={handleTabSelect}>
        <TabList className={styles["tab-list"]}>
          {tabTitles.map((title, index) => (
            <Tab
              key={tabIds[index]}
              className={styles["tab-list-name"]}
              selectedClassName={styles["tab-list-name--selected"]}
            >
              {title}
            </Tab>
          ))}
        </TabList>
        <TabPanel key={tabIds[0]!} className={styles["custom-tab-panel"]}>
          <ListContent />
        </TabPanel>
        <TabPanel key={tabIds[1]!} className={styles["custom-tab-panel"]}>
          <MemberList />
        </TabPanel>
        <TabPanel key={tabIds[2]!} className={styles["custom-tab-panel"]}>
          <GuestList />
        </TabPanel>
      </Tabs>
    </div>
  );
};

export default OrganizationTop;
